import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import {
  useCreateEditorCompetencyAreaMutation,
  useUpdateEditorCompetencyMutation,
} from '../../redux/growth-wheel.endpoints'
import type {
  CompetencyArea,
  Competency,
  WheelImageUploadControls,
} from '../../types/GrowthWheelManagement'
import '../../css/gw-wheel-management.scss'
import { WheelManagementImageCard } from './WheelManagementImageCard'
import { AddCompetencyForm } from './AddCompetencyForm'
import { WheelManagementAreaCard } from './WheelManagementAreaCard'
import { WheelManagementDeleteModal } from './WheelManagementDeleteModal'
import {
  EditSubCompetencyPanel,
  competencyEditableContentEqual,
} from './EditSubCompetencyPanel'
import { buildSubCompetencyEditWarningModal } from './wheelManagementWarningModalService'

type WarningModalState = {
  kind: 'subCompetencyEdit'
  competency: Competency
}

interface WheelManagementProps {
  growthWheelId: number
  areas: CompetencyArea[]
  competencies: Competency[]
  roleName?: string
  wheelImageUrl?: string | null
  wheelImageUpload?: WheelImageUploadControls
  onUpdateArea: (areaId: string, updates: Partial<CompetencyArea>) => void
  onDeleteArea: (areaId: string) => void
  onAddArea: (area: Omit<CompetencyArea, 'id'>) => void
  onUpdateCompetency: (
    competencyId: string,
    updates: Partial<Competency>,
  ) => void | Promise<void>
  onDeleteCompetency: (competencyId: string) => void
  onAddCompetency: (competency: Omit<Competency, 'id'>) => void
  onReorderAreas: (areas: CompetencyArea[]) => void
  onReorderCompetencies: (
    areaId: string,
    competenciesInAreaOrder: Competency[],
  ) => void
}

export function WheelManagement({
  growthWheelId,
  areas,
  competencies,
  roleName,
  wheelImageUrl,
  wheelImageUpload,
  onUpdateArea,
  onDeleteArea,
  onAddArea,
  onUpdateCompetency,
  onDeleteCompetency,
  onAddCompetency,
  onReorderAreas,
  onReorderCompetencies,
}: WheelManagementProps) {
  const [createArea] = useCreateEditorCompetencyAreaMutation()
  const [updateCompetency] = useUpdateEditorCompetencyMutation()

  const [showAddArea, setShowAddArea] = useState(false)
  const [newAreaName, setNewAreaName] = useState('')
  const [newAreaDesc, setNewAreaDesc] = useState('')

  const [warningModal, setWarningModal] = useState<WarningModalState | null>(
    null,
  )

  /** Index of the area card currently highlighted as a drop target (area reorder). */
  const [areaDropTargetIndex, setAreaDropTargetIndex] = useState<
    number | null
  >(null)

  const [editingCompetency, setEditingCompetency] = useState<Competency | null>(
    null,
  )
  const [editedCompetency, setEditedCompetency] = useState<Competency | null>(
    null,
  )

  const handleAddArea = async () => {
    if (!newAreaName.trim()) return
    await createArea({
      growthWheelId,
      body: {
        name: newAreaName.trim(),
        description: newAreaDesc.trim(),
      },
    }).unwrap()
    setNewAreaName('')
    setNewAreaDesc('')
    setShowAddArea(false)
  }

  const startEditCompetency = (competency: Competency) => {
    setEditingCompetency(competency)
    setEditedCompetency({ ...competency })
  }

  const saveCompetencyEdit = () => {
    if (
      !editedCompetency ||
      !editingCompetency ||
      !editedCompetency.name.trim() ||
      competencyEditableContentEqual(editedCompetency, editingCompetency)
    ) {
      return
    }
    setWarningModal({
      kind: 'subCompetencyEdit',
      competency: { ...editedCompetency },
    })
  }

  const confirmWarningModal = async () => {
    if (!warningModal) return

    if (warningModal.kind === 'subCompetencyEdit') {
      await updateCompetency({
        growthWheelId,
        competencyId: Number(warningModal.competency.id),
        body: {
          name: warningModal.competency.name,
          description: warningModal.competency.description,
          levelDescriptions: warningModal.competency.levelDescriptions,
        },
      }).unwrap()
      setEditingCompetency(null)
      setEditedCompetency(null)
      setWarningModal(null)
    }
  }

  const closeEditPanel = () => {
    setEditingCompetency(null)
    setEditedCompetency(null)
  }

  const warningContent =
    warningModal?.kind === 'subCompetencyEdit'
      ? buildSubCompetencyEditWarningModal(warningModal.competency.name)
      : null

  return (
    <div className="gw-wm">
      <div>
        <h1 className="gw-wm__page-title">Growth Wheel Management</h1>
        <p className="gw-wm__lead">
          Configure the competency framework structure and content for your
          organization.
        </p>
      </div>

      <WheelManagementImageCard
        wheelImageUrl={wheelImageUrl}
        roleName={roleName}
        wheelImageUpload={wheelImageUpload}
      />

      <div className="gw-wm__card">
        <div className="gw-wm__framework-head">
          <div>
            <h2 className="gw-wm__card-title" style={{ marginBottom: 0 }}>
              Competencies
            </h2>
          </div>
          <button
            type="button"
            className="gw-wm__btn-primary"
            onClick={() => setShowAddArea(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Competency
          </button>
        </div>

        {areas.length === 0 && !showAddArea && (
          <div className="gw-wm__empty">
            <div className="gw-wm__empty-icon">
              <FontAwesomeIcon icon={faPlus} />
            </div>
            <p className="mb-3">
              No competencies yet. Add a competency to get started.
            </p>
          </div>
        )}

        {showAddArea && (
          <AddCompetencyForm
            newAreaName={newAreaName}
            newAreaDesc={newAreaDesc}
            onChangeName={setNewAreaName}
            onChangeDesc={setNewAreaDesc}
            onSubmit={() => void handleAddArea()}
            onCancel={() => {
              setShowAddArea(false)
              setNewAreaName('')
              setNewAreaDesc('')
            }}
          />
        )}

        <div
          className="gw-wm__area-list"
          onDragLeave={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setAreaDropTargetIndex(null)
            }
          }}
        >
          {areas.map((area, index) => (
            <WheelManagementAreaCard
              key={area.id}
              growthWheelId={growthWheelId}
              area={area}
              index={index}
              allAreas={areas}
              allCompetencies={competencies}
              onEditCompetency={startEditCompetency}
              areaDropTargetIndex={areaDropTargetIndex}
              onAreaDropTargetChange={setAreaDropTargetIndex}
            />
          ))}
        </div>
      </div>

      {editingCompetency && editedCompetency && (
        <EditSubCompetencyPanel
          editedCompetency={editedCompetency}
          baselineCompetency={editingCompetency}
          setEditedCompetency={setEditedCompetency}
          roleName={roleName}
          parentAreaName={areas.find(a => a.id === editedCompetency.area)?.name}
          onClose={closeEditPanel}
          onSave={saveCompetencyEdit}
        />
      )}

      {warningModal?.kind === 'subCompetencyEdit' && (
        <WheelManagementDeleteModal
          tone="warning"
          title={warningContent?.title ?? ''}
          confirmLabel={warningContent?.confirmLabel ?? ''}
          onCancel={() => setWarningModal(null)}
          onConfirm={() => void confirmWarningModal()}
          irreversibleMessage="This reset cannot be undone."
        >
          <p className="gw-wm__hint">{warningContent?.description}</p>
        </WheelManagementDeleteModal>
      )}
    </div>
  )
}
